<!-- A ready summary with no section to draw used to leave the card body blank. -->
<div class="no-data animation-fade-in" *ngIf="isEmpty">No data available</div>

<ng-container *ngIf="content">
	<div class="one-paragraph" *ngIf="oneParagraph">
		<typography variant="p/xxs_semibold" class="one-paragraph__label">
			In one paragraph
		</typography>
		<typography variant="p/base_regular" class="one-paragraph__text">
			<ng-container *ngFor="let segment of oneParagraphSegments">
				<strong *ngIf="segment.bold">{{ segment.text }}</strong>
				<ng-container *ngIf="!segment.bold && !segment.italic">
					{{ segment.text }}
				</ng-container>
			</ng-container>
		</typography>
	</div>

	<section class="content-section" *ngIf="kpis.length">
		<typography variant="h/2" class="section-title">
			{{ kpiSectionTitle }}
		</typography>
		<div class="kpi-grid">
			<app-sl-kpi-card
				*ngFor="let metric of kpis"
				[label]="metric.label"
				[value]="metric.value"
				[change]="metric.changes ?? null"
				[good]="metric.good ?? true"
			></app-sl-kpi-card>
		</div>
	</section>

	<section class="content-section" *ngIf="shareOfVoiceRows.length">
		<typography variant="h/2" class="section-title">
			Share of voice
		</typography>
		<table class="comparison-table">
			<thead>
				<tr>
					<th>Competitor</th>
					<th>Mentions</th>
					<th>Share</th>
					<th>Reach</th>
					<th>Eng. rate</th>
					<th>Positive</th>
					<th>Negative</th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let row of shareOfVoiceRows">
					<td class="comparison-table__name">{{ row.name }}</td>
					<td>{{ row.mentions | number }}</td>
					<td>{{ row.share | number: '1.0-1' }}%</td>
					<td>{{ row.reach | number }}</td>
					<td>{{ row.engagement_rate | number: '1.0-2' }}%</td>
					<td class="comparison-table__positive">
						{{ row.positive_pct | number: '1.0-1' }}%
					</td>
					<td class="comparison-table__negative">
						{{ row.negative_pct | number: '1.0-1' }}%
					</td>
				</tr>
			</tbody>
		</table>
	</section>

	<section class="content-section" *ngIf="positioningRows.length">
		<typography variant="h/2" class="section-title">
			Competitive positioning
		</typography>
		<table class="comparison-table">
			<thead>
				<tr>
					<th>Business</th>
					<th>Type</th>
					<th>Mentions</th>
					<th>Share</th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let row of positioningRows">
					<td class="comparison-table__name">{{ row.business }}</td>
					<td>{{ row.business_type | titlecase }}</td>
					<td>{{ row.mentions | number }}</td>
					<td>{{ row.share | number: '1.0-1' }}%</td>
				</tr>
			</tbody>
		</table>
	</section>

	<img *ngIf="imageUrl" class="summary-cover-image" [src]="imageUrl" alt="" />

	<section class="content-section" *ngIf="unfoldedText">
		<typography variant="h/2" class="section-title">
			What unfolded this period
		</typography>
		<typography variant="p/base_regular" class="section-text">
			{{ unfoldedText }}
		</typography>
	</section>

	<section class="content-section" *ngIf="isReviewsEnabled">
		<typography variant="h/2" class="section-title">
			What reviews are saying
		</typography>

		<app-ai-summary-rating-distribution
			*ngIf="showRatingDistribution"
			[averageRating]="reviewsBlock.average_rating"
			[reviewCount]="reviewsBlock.review_count"
			[distribution]="ratingDistribution"
		></app-ai-summary-rating-distribution>

		<!-- Enabled (a review source is connected) with nothing to show left
		     the heading over an empty section. -->
		<div
			class="no-data no-data--section animation-fade-in"
			*ngIf="reviewsEmpty"
		>
			No data available
		</div>

		<typography
			variant="p/base_regular"
			class="section-text"
			*ngIf="reviewsBlock.text"
		>
			{{ reviewsBlock.text }}
		</typography>

		<blockquote
			class="review-highlight"
			*ngIf="reviewsBlock.standout_review"
		>
			<typography variant="p/base_regular" class="review-highlight__body">
				{{ reviewsBlock.standout_review.body }}
			</typography>
			<div class="review-highlight__footer">
				<div class="review-highlight__stars">
					<ng-container *ngFor="let filled of highlightReviewStars">
						<app-icon
							type="svg"
							[size]="16"
							[color]="filled ? '#e0a82e' : '#d0d0d0'"
						>
							{{ filled ? 'star_filled' : 'star_outline' }}
						</app-icon>
					</ng-container>
				</div>
				<typography
					variant="p/base_regular"
					class="review-highlight__author"
				>
					{{ reviewsBlock.standout_review.author_name }}
				</typography>
				<typography
					variant="p/base_regular"
					class="review-highlight__platform"
				>
					{{ reviewsBlock.standout_review.platform }}
				</typography>
			</div>
		</blockquote>
	</section>

	<section class="content-section" *ngIf="conversationItems.length">
		<typography variant="h/2" class="section-title">
			What moved the conversation
		</typography>
		<div class="conversation-list">
			<div
				class="conversation-item"
				*ngFor="let item of conversationItems"
			>
				<typography variant="p/base_regular">
					<strong class="conversation-item__title">
						{{ item.title }}
					</strong>
					{{ item.text }}
				</typography>
			</div>
		</div>
	</section>

	<section class="content-section" *ngIf="languageTerms.length">
		<typography variant="h/2" class="section-title">
			The language people use
		</typography>
		<typography variant="p/base_regular" class="section-text">
			Top organic descriptors used across mentions and reviews. Source
			label shows whether the word skews to one stream.
		</typography>
		<app-ai-summary-language-table
			[terms]="languageTerms"
		></app-ai-summary-language-table>
		<typography
			variant="p/base_regular"
			class="language-text"
			*ngIf="languageTextSegments.length"
		>
			<ng-container *ngFor="let segment of languageTextSegments">
				<strong *ngIf="segment.bold">{{ segment.text }}</strong>
				<em *ngIf="segment.italic">{{ segment.text }}</em>
				<ng-container *ngIf="!segment.bold && !segment.italic">
					{{ segment.text }}
				</ng-container>
			</ng-container>
		</typography>
	</section>

	<section class="content-section" *ngIf="topMention || topReview">
		<typography variant="h/2" class="section-title">
			Standout voices this week
		</typography>
		<typography variant="p/base_regular" class="section-text">
			One mention and one review that capture the split.
		</typography>
		<div class="standout-voices">
			<div
				class="voice-card"
				[class.voice-card--negative]="mentionIsNegative"
				*ngIf="topMention"
			>
				<typography variant="p/xxs_semibold" class="voice-card__label">
					Top mention
				</typography>
				<typography variant="p/base_regular" class="voice-card__body">
					"{{ topMention.content }}"
				</typography>
				<div class="voice-card__footer">
					<typography
						variant="p/base_regular"
						class="voice-card__meta"
					>
						{{ topMention.author_name
						}}{{
							topMention.platform
								? ' · ' + topMention.platform
								: ''
						}}
					</typography>
					<typography
						variant="p/base_regular"
						class="voice-card__stat"
						*ngIf="topMention.likes"
					>
						{{ topMentionLikesFormatted }}
					</typography>
				</div>
			</div>
			<div
				class="voice-card"
				[class.voice-card--negative]="reviewIsNegative"
				*ngIf="topReview"
			>
				<typography variant="p/xxs_semibold" class="voice-card__label">
					Top review
				</typography>
				<typography variant="p/base_regular" class="voice-card__body">
					"{{ topReview.body }}"
				</typography>
				<div class="voice-card__footer">
					<div class="voice-card__author-meta">
						<div class="voice-card__stars">
							<ng-container *ngFor="let filled of topReviewStars">
								<app-icon
									type="svg"
									[size]="16"
									[color]="filled ? '#e0a82e' : '#d0d0d0'"
								>
									{{
										filled ? 'star_filled' : 'star_outline'
									}}
								</app-icon>
							</ng-container>
						</div>
						<div class="voice-card__author-info">
							<typography
								variant="p/base_regular"
								class="voice-card__author"
							>
								{{ topReview.author_name }}
							</typography>
							<typography
								variant="p/base_regular"
								class="voice-card__verified"
								*ngIf="topReview.author_verified"
							>
								· Verified buyer
							</typography>
						</div>
					</div>
					<typography
						variant="p/base_regular"
						class="voice-card__meta"
					>
						{{ topReview.platform }}
					</typography>
				</div>
			</div>
		</div>
	</section>

	<section
		class="content-section"
		*ngIf="sources?.channels?.length || sources?.review_sources?.length"
	>
		<typography variant="h/2" class="section-title">
			Where it is coming from
		</typography>
		<app-ai-summary-sources
			[channels]="sources.channels"
			[reviewSources]="sources.review_sources"
		></app-ai-summary-sources>
		<typography
			variant="p/base_regular"
			class="section-text"
			*ngIf="sourcesTextSegments.length"
		>
			<ng-container *ngFor="let segment of sourcesTextSegments">
				<strong *ngIf="segment.bold">{{ segment.text }}</strong>
				<ng-container *ngIf="!segment.bold && !segment.italic">
					{{ segment.text }}
				</ng-container>
			</ng-container>
		</typography>
	</section>

	<section class="content-section" *ngIf="signals.length">
		<typography variant="h/2" class="section-title">
			Signals to watch
		</typography>
		<app-ai-summary-signal-card
			*ngFor="let signal of signals"
			[signal]="signal"
		></app-ai-summary-signal-card>
	</section>
</ng-container>
